import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const PERM_TO_ROUTE = [
  ["fornitori:view", "/fornitori"],
  ["prodotti:view", "/prodotti"],
  ["ordini:view", "/ordini"],
  ["movimentazioni:view", "/movimentazioni"],
  ["impostazioni:view", "/impostazioni"],
];

/**
 * Hook returning a function that checks if the current user has a permission.
 * Admin role implicitly has all permissions.
 */
export function useHasPerm() {
  const { user } = useAuth();
  return (perm) => {
    if (!perm) return true;
    if (!user) return false;
    if (user.role === "admin") return true;
    const list = user.permessi || [];
    return list.includes(perm);
  };
}

/**
 * Find the first allowed route for a user based on their permissions.
 */
function findLandingRoute(user) {
  if (!user) return "/login";
  if (user.role === "admin") return "/";
  const perms = user.permessi || [];
  if (perms.includes("dashboard:view")) return "/";
  for (const [p, r] of PERM_TO_ROUTE) {
    if (perms.includes(p)) return r;
  }
  return null;
}

/**
 * Component that gates a route by permission.
 * - default fallback: redirect to "/" (dashboard)
 * - fallback="auto": redirect to first allowed page based on user perms
 */
export default function PermissionGate({ perm, children, fallback }) {
  const has = useHasPerm();
  const { user } = useAuth();
  if (!perm) return children;
  if (has(perm)) return children;
  if (fallback === "auto") {
    const target = findLandingRoute(user);
    if (target && target !== "/") return <Navigate to={target} replace />;
    return (
      <div className="ds-card p-8 text-center" data-testid="no-permissions">
        <h2 className="font-brand text-2xl font-light">Nessun permesso assegnato</h2>
        <p className="mt-3 text-sm" style={{ color: "var(--ds-text-secondary)" }}>
          Il tuo ruolo non ha permessi attivi. Contatta un amministratore.
        </p>
      </div>
    );
  }
  return <Navigate to="/" replace />;
}
